import type { DateTimeFilterBounds } from '@/shared/lib/dateTimeFilter';
import { useRecordActions } from '@/features/RecordActions/model/useRecordActions';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ORDER_PROBLEM_COLUMNS } from '@/entities/OrderProblem/config/order-problem.config';
import { useOrderProblems } from '@/entities/OrderProblem/model/store';
import { RequestStatus } from '@/shared/model/requestStatus';
import { EntityActionPanel } from '@/shared/ui/EntityActionPanel/EntityActionPanel';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { DataTable } from '@/shared/ui/DataTable/DataTable';
import { InputText } from '@/shared/ui/InputText/InputText';
import { RequestErrorBanner } from '@/shared/ui/RequestState';
import classes from './OrderProblemsPage.module.scss';

/** Отображает проблемные заказы и историю устранённых проблем */
export const OrderProblemsPage = () => {
    const recordActions = useRecordActions('orderProblems', () => refresh());

    const navigate = useNavigate();
    const [resolved, setResolved] = useState(false);
    const [invoice, setInvoice] = useState('');
    const [search, setSearch] = useState('');
    const [dateRange, setDateRange] = useState<DateTimeFilterBounds>({});
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(25);
    const items = useOrderProblems((state) => state.items);
    const allCount = useOrderProblems((state) => state.allCount);
    const activeCount = useOrderProblems((state) => state.activeCount);
    const status = useOrderProblems((state) => state.listStatus);
    const error = useOrderProblems((state) => state.listError);
    const fetchList = useOrderProblems((state) => state.fetchList);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setFirst(0);
            setSearch(invoice);
        }, 300);

        return () => window.clearTimeout(timer);
    }, [invoice]);

    const refresh = useCallback(() => fetchList({
        lastOccurredFrom: dateRange.from,
        lastOccurredTo: dateRange.to,
        isResolved: resolved,
        externalId: search || undefined,
        skipCount: first,
        takeCount: rows,
        ascending: false,
    }), [fetchList, resolved, search, first, rows, dateRange]);

    useEffect(() => {
        void refresh();
        const timer = window.setInterval(() => void refresh(), 30_000);

        return () => window.clearInterval(timer);
    }, [refresh]);

    return <div className={classes.page}>
        <EntityActionPanel
            name="Проблемные заказы"
            variant={activeCount > 0
                ? 'warning'
                : 'default'
            }
            buttons={<ActionButton
                label="Обновить"
                icon="pi pi-refresh"
                onClick={() => void refresh()}
                loading={status === RequestStatus.Loading}
            />}
        >
            <p>Активных проблем: {activeCount}. Здесь сохраняются ошибки заказа, оплаты и кэшбэка.</p>
        </EntityActionPanel>
        <div className={classes.toolbar}>
            <ActionButton
                label="Требуют внимания"
                outlined={resolved}
                aria-pressed={!resolved}
                onClick={() => {
                    setResolved(false);
                    setFirst(0);
                }}
            />
            <ActionButton
                label="Устранённые"
                outlined={!resolved}
                aria-pressed={resolved}
                onClick={() => {
                    setResolved(true);
                    setFirst(0);
                }}
            />
            <InputText
                name="problemInvoice"
                label="InvId Robokassa"
                value={invoice}
                onChange={(event) => setInvoice(event.target.value)}
                placeholder="Найти подтверждение оплаты"
                numeric
            />
        </div>
        <RequestErrorBanner
            status={status}
            error={error}
            fallbackMessage="Не удалось загрузить проблемные заказы"
            onRetry={refresh}
        />
        <DataTable
            onDateFilterChange={(_field, bounds) => {
                setDateRange(bounds ?? {});
                setFirst(0);
            }}
            rowActions={recordActions.getMenuItems}
            getRowUrl={item => item.id
                ? `/order-problems/${encodeURIComponent(item.id)}`
                : undefined}
            data={items}
            columns={ORDER_PROBLEM_COLUMNS}
            loading={status === RequestStatus.Loading}
            onRowClick={(problem) => problem.id && navigate(`/order-problems/${problem.id}`)}
            paginator
            lazy
            first={first}
            rows={rows}
            totalRecords={allCount}
            onPageChange={(event) => {
                setFirst(event.first);
                setRows(event.rows);
            }}
            emptyMessage={resolved
                ? 'Устранённых проблем пока нет'
                : 'Активных проблем нет'
            }
        />
        {recordActions.dialogs}
    </div>;
};
